import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill, type StatusVariant } from '../patterns';
import { formatQty, formatPct, selectDisplayUnit, convertToUnit } from '../../utils/fertilizerStockUsageMath';
import type { EnrichedFertilizerStockUsageRow, VarianceStatus } from '../../utils/fertilizerStockUsageMath';

export interface PlannedVsActualMeterProps {
  row: EnrichedFertilizerStockUsageRow;
}

const VARIANCE_VARIANT: Record<VarianceStatus, StatusVariant> = {
  ontarget:   'ok',
  overspend:  'danger',
  underspend: 'info',
};

const VARIANCE_LABEL_KEY: Record<VarianceStatus, string> = {
  ontarget:   'nutrition.dashboard.fertilizerStockUsage.status.ontarget',
  overspend:  'nutrition.dashboard.fertilizerStockUsage.status.overspend',
  underspend: 'nutrition.dashboard.fertilizerStockUsage.status.underspend',
};

const VARIANCE_CAP = 0.5;

export const PlannedVsActualMeter: React.FC<PlannedVsActualMeterProps> = ({ row }) => {
  const { t } = useTranslation();

  if (row.cPlanned === 0) {
    return (
      <p className="fsu-meter-empty">
        {t('nutrition.dashboard.fertilizerStockUsage.empty.noCompletedSessions')}
      </p>
    );
  }

  // Pick one display unit for both Planned and Actual based on whichever is
  // larger. Sub-1 L → ml; sub-1 kg → g; everything else keeps its source unit.
  const dUnit     = selectDisplayUnit([row.cPlanned, row.cActual], row.unit);
  const dPlanned  = convertToUnit(row.cPlanned, row.unit, dUnit);
  const dActual   = convertToUnit(row.cActual,  row.unit, dUnit);

  const dev   = Math.max(-VARIANCE_CAP, Math.min(VARIANCE_CAP, row.variancePct));
  const left  = dev >= 0 ? '50%' : `${50 + (dev / VARIANCE_CAP) * 50}%`;
  const width = `${Math.abs(dev / VARIANCE_CAP * 50)}%`;
  const variant   = VARIANCE_VARIANT[row.varianceStatus];
  const pillLabel = t(VARIANCE_LABEL_KEY[row.varianceStatus]);

  return (
    <div className="fsu-meter fsu-meter--variance">
      <div className="fsu-meter-numbers">
        <div className="fsu-num">
          <span className="fsu-num-label">{t('nutrition.dashboard.fertilizerStockUsage.label.planned')}</span>
          <span className="fsu-num-val">
            {formatQty(dPlanned)} <span className="fsu-num-unit">{dUnit}</span>
          </span>
        </div>
        <div className="fsu-num">
          <span className="fsu-num-label">{t('nutrition.dashboard.fertilizerStockUsage.label.actual')}</span>
          <span className="fsu-num-val">
            {formatQty(dActual)} <span className="fsu-num-unit">{dUnit}</span>
          </span>
          <span className={`fsu-num-sub fsu-variance-pct fsu-variance-pct--${row.varianceStatus}`}>
            {formatPct(row.variancePct)}
          </span>
        </div>
        <span className={`fsu-pill-with-dot fsu-pill-with-dot--${row.varianceStatus}`}>
          <StatusPill variant={variant}>{pillLabel}</StatusPill>
        </span>
      </div>
      <div className="fsu-variance">
        <div className="fsu-variance-axis">
          <span>−{Math.round(VARIANCE_CAP * 100)}%</span>
          <span>0%</span>
          <span>+{Math.round(VARIANCE_CAP * 100)}%</span>
        </div>
        <div className="fsu-variance-track">
          <div className="fsu-variance-tol" />
          <div className="fsu-variance-zero" />
          <div className={`fsu-variance-bar fsu-variance-bar--${row.varianceStatus}`} style={{ left, width }} />
        </div>
      </div>
    </div>
  );
};

export default PlannedVsActualMeter;
